import { historyHourStarts, type WeatherHistory, type WeatherNow } from './types';

export const WEATHER_PRESET_NAMES = ['clear', 'cloudy', 'overcast', 'drizzle', 'rain', 'storm', 'snow', 'sleet', 'fog'] as const;
export type WeatherPresetName = (typeof WEATHER_PRESET_NAMES)[number];

export interface WeatherOverride {
  preset?: WeatherPresetName | undefined;
  temperatureC?: number | undefined;
  windSpeedMs?: number | undefined;
  clouds?: number | undefined;
}

type Preset = Omit<WeatherNow, 'source' | 'rainMm' | 'snowfallCm'>;

// code: Open-Meteo WMO subset (sleet has no exact code; 66 = light freezing rain is the closest).
const PRESETS: Record<WeatherPresetName, Preset> = {
  clear:    { temperatureC: 18, humidity: 0.6,  visibilityM: 30000, precipitationMm: 0,   cloudLow: 0.05, cloudMid: 0.05, cloudHigh: 0.1,  windSpeedMs: 2,  windDirDeg: 250, gustMs: 4,  code: 0 },
  cloudy:   { temperatureC: 16, humidity: 0.7,  visibilityM: 20000, precipitationMm: 0,   cloudLow: 0.4,  cloudMid: 0.5,  cloudHigh: 0.4,  windSpeedMs: 3,  windDirDeg: 250, gustMs: 6,  code: 2 },
  overcast: { temperatureC: 14, humidity: 0.8,  visibilityM: 12000, precipitationMm: 0,   cloudLow: 0.95, cloudMid: 0.9,  cloudHigh: 0.6,  windSpeedMs: 3,  windDirDeg: 250, gustMs: 6,  code: 3 },
  drizzle:  { temperatureC: 13, humidity: 0.93, visibilityM: 6000,  precipitationMm: 0.3, cloudLow: 1,    cloudMid: 0.9,  cloudHigh: 0.5,  windSpeedMs: 2,  windDirDeg: 250, gustMs: 4,  code: 53 },
  rain:     { temperatureC: 12, humidity: 0.95, visibilityM: 4000,  precipitationMm: 3,   cloudLow: 1,    cloudMid: 1,    cloudHigh: 0.7,  windSpeedMs: 5,  windDirDeg: 220, gustMs: 10, code: 63 },
  storm:    { temperatureC: 22, humidity: 0.95, visibilityM: 3000,  precipitationMm: 12,  cloudLow: 1,    cloudMid: 1,    cloudHigh: 1,    windSpeedMs: 9,  windDirDeg: 200, gustMs: 20, code: 95 },
  snow:     { temperatureC: -1, humidity: 0.9,  visibilityM: 2500,  precipitationMm: 1.5, cloudLow: 1,    cloudMid: 1,    cloudHigh: 0.6,  windSpeedMs: 3,  windDirDeg: 320, gustMs: 6,  code: 73 },
  sleet:    { temperatureC: 1,  humidity: 0.95, visibilityM: 3000,  precipitationMm: 2,   cloudLow: 1,    cloudMid: 1,    cloudHigh: 0.6,  windSpeedMs: 4,  windDirDeg: 320, gustMs: 8,  code: 66 },
  fog:      { temperatureC: 10, humidity: 1,    visibilityM: 300,   precipitationMm: 0,   cloudLow: 1,    cloudMid: 0.3,  cloudHigh: 0.1,  windSpeedMs: 1,  windDirDeg: 250, gustMs: 2,  code: 45 },
};

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));

/** Split precipitation into rain/snow by temperature (Open-Meteo convention: snowfall cm ≈ 0.7 × water mm). */
export function splitPrecipitation(precipitationMm: number, temperatureC: number): { rainMm: number; snowfallCm: number } {
  if (precipitationMm <= 0) return { rainMm: 0, snowfallCm: 0 };
  if (temperatureC <= 0.5) return { rainMm: 0, snowfallCm: precipitationMm * 0.7 };
  if (temperatureC < 2.5) return { rainMm: precipitationMm * 0.5, snowfallCm: precipitationMm * 0.35 };
  return { rainMm: precipitationMm, snowfallCm: 0 };
}

export function overrideWeather(o: WeatherOverride): WeatherNow {
  const p = PRESETS[o.preset ?? 'clear'];
  const temperatureC = o.temperatureC ?? p.temperatureC;
  const windSpeedMs = o.windSpeedMs ?? p.windSpeedMs;
  const clouds = o.clouds === undefined ? undefined : clamp01(o.clouds);
  return {
    ...p,
    temperatureC,
    windSpeedMs,
    gustMs: Math.max(p.gustMs, windSpeedMs * 1.6),
    cloudLow: clouds ?? p.cloudLow,
    cloudMid: clouds ?? p.cloudMid,
    cloudHigh: clouds ?? p.cloudHigh,
    ...splitPrecipitation(p.precipitationMm, temperatureC),
    source: 'override',
  };
}

export function overrideHistory(instant: number, o: WeatherOverride): WeatherHistory {
  const now = overrideWeather(o);
  return { hours: historyHourStarts(instant).map((time) => ({ ...now, time })), daily: [] };
}
